import { DynamicWorkflowWrite } from './dynamic-framework.models';

export type WorkflowIssueSeverity = 'error' | 'warning';

export interface WorkflowIssue {
  severity: WorkflowIssueSeverity;
  message: string;
  nodeKey?: string;
}

export interface WorkflowValidationSummary {
  issues: WorkflowIssue[];
  errorCount: number;
  warningCount: number;
  errorNodeKeys: Set<string>;
}

/** Mirrors the server's structural and publish-time graph checks. */
export function validateWorkflowDraft(
  draft: DynamicWorkflowWrite,
  startNodeKey?: string,
): WorkflowValidationSummary {
  const issues: WorkflowIssue[] = [];
  const byKey = new Map(draft.nodes.map(node => [node.key, node]));

  draft.nodes
    .filter(node => !node.label.trim())
    .forEach(node => issues.push({
      severity: 'error',
      message: 'Node name is required.',
      nodeKey: node.key,
    }));

  const seenNodes = new Set<string>();
  draft.nodes.forEach(node => {
    if (seenNodes.has(node.key)) {
      issues.push({
        severity: 'error',
        message: `Node key "${node.key}" is used more than once.`,
        nodeKey: node.key,
      });
      return;
    }
    seenNodes.add(node.key);
  });

  draft.transitions.forEach(edge => {
    const from = byKey.get(edge.fromNodeKey);
    const to = byKey.get(edge.toNodeKey);
    if (!from || !to) {
      issues.push({
        severity: 'error',
        message: `A transition points at a node that does not exist (${
          !from ? edge.fromNodeKey : edge.toNodeKey}).`,
      });
    }
  });

  const seenTransitions = new Set<string>();
  draft.transitions.forEach(edge => {
    const pair = `${edge.fromNodeKey}\0${edge.toNodeKey}`;
    if (seenTransitions.has(pair)) {
      issues.push({
        severity: 'error',
        message: `"${label(byKey, edge.fromNodeKey)} → ${
          label(byKey, edge.toNodeKey)}" is defined more than once.`,
      });
      return;
    }
    seenTransitions.add(pair);
  });

  const disconnected = byKey.size === draft.nodes.length
    ? disconnectedNodeKeys(draft)
    : new Set<string>();
  disconnected.forEach(nodeKey => issues.push({
    severity: 'error',
    message: `Node "${label(byKey, nodeKey)}" is disconnected from the workflow.`,
    nodeKey,
  }));

  if (startNodeKey && byKey.has(startNodeKey)) {
    unreachableNodeKeys(draft, startNodeKey).forEach(nodeKey => {
      if (disconnected.has(nodeKey)) return;
      issues.push({
        severity: 'error',
        message: `Node "${label(byKey, nodeKey)}" cannot be reached from the selected start node.`,
        nodeKey,
      });
    });
  }

  const errors = issues.filter(issue => issue.severity === 'error');
  return {
    issues,
    errorCount: errors.length,
    warningCount: issues.length - errors.length,
    errorNodeKeys: new Set(
      errors.map(issue => issue.nodeKey).filter((key): key is string => !!key),
    ),
  };
}

function disconnectedNodeKeys(draft: DynamicWorkflowWrite): Set<string> {
  if (draft.nodes.length <= 1) return new Set<string>();
  const keys = new Set(draft.nodes.map(node => node.key));
  const neighbours = new Map([...keys].map(key => [key, [] as string[]]));
  draft.transitions.forEach(edge => {
    if (!keys.has(edge.fromNodeKey) || !keys.has(edge.toNodeKey)) return;
    neighbours.get(edge.fromNodeKey)?.push(edge.toNodeKey);
    neighbours.get(edge.toNodeKey)?.push(edge.fromNodeKey);
  });

  const remaining = new Set(keys);
  const components: Set<string>[] = [];
  while (remaining.size) {
    const component = traverse(remaining.values().next().value!, neighbours);
    components.push(component);
    component.forEach(key => remaining.delete(key));
  }
  if (components.length === 1) return new Set<string>();

  const primary = components.reduce((largest, item) =>
    item.size > largest.size ? item : largest);
  return new Set([...keys].filter(key => !primary.has(key)));
}

function unreachableNodeKeys(draft: DynamicWorkflowWrite, startNodeKey: string): Set<string> {
  const keys = new Set(draft.nodes.map(node => node.key));
  const outgoing = new Map([...keys].map(key => [key, [] as string[]]));
  draft.transitions.forEach(edge => {
    if (keys.has(edge.fromNodeKey) && keys.has(edge.toNodeKey)) {
      outgoing.get(edge.fromNodeKey)?.push(edge.toNodeKey);
    }
  });
  const reachable = traverse(startNodeKey, outgoing);
  return new Set([...keys].filter(key => !reachable.has(key)));
}

function traverse(start: string, edges: ReadonlyMap<string, string[]>): Set<string> {
  const visited = new Set([start]);
  const pending = [start];
  while (pending.length) {
    const current = pending.shift()!;
    edges.get(current)?.forEach(next => {
      if (visited.has(next)) return;
      visited.add(next);
      pending.push(next);
    });
  }
  return visited;
}

function label(byKey: Map<string, { label: string }>, key: string): string {
  return byKey.get(key)?.label ?? key;
}
